import { ColumnDef, SortingFn } from '@tanstack/react-table';
import { BaseCell } from 'src/components/TableV2/cells';
import { DateCell } from 'src/components/TableV2/cells/DateCell';
import {
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { FormatOption } from 'src/modals/EditTableColumns';
import { TableShimmeringDiv } from 'src/tables/Table';
import { safeSortDateTime } from 'src/utils/tableUtils';
import { SectionType } from 'src/utils/types/sectionType';

import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from '../../SalesTable/SalesTable.type';

interface DateColumnOptions<TData> {
  /** Column ID */
  id: string;
  /** Content ID for the header label */
  headerContentId: string;
  /** Function to extract date string from data */
  accessor: (data: TData) => string | null | undefined;
  /** Default precision if user hasn't set one */
  defaultPrecision?: FormatOption;
  /** Column size */
  size?: number;
  /** Enable UTC fallback for date parsing */
  enableUtcFallback?: boolean;
  /** Hide relative terms (e.g., "2h ago") */
  hideRelativeTerms?: boolean;
  sectionType?: SectionType; // For useColumnUserSetting hook
  /** Custom sorting function */
  sortingFn?: SortingFn<TData>;
  /** Function to determine if cell should be rendered (return null if false) */
  shouldRender?: (data: TData) => boolean;
  /** Function to determine shimmer height based on data */
  getShimmerHeight?: (data: TData) => string | number;
  /** Function to determine if data is still loading */
  isLoading?: (data: TData) => boolean;
}

/**
 * Factory function to create date column definitions for sales tables.
 *
 * @example
 * createDateColumn({
 *   id: SalesTableColumnId.OrderDate,
 *   headerContentId: SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.OrderDate],
 *   accessor: (data) => data?.sale?.saleDate,
 *   defaultPrecision: FormatOption.DateTime_MonthDD_Time,
 * })
 */
export const createDateColumn = <TData,>(
  options: DateColumnOptions<TData>
): ColumnDef<TData> => {
  const {
    id,
    headerContentId,
    accessor,
    defaultPrecision = FormatOption.DateTime_MonthDD_Time,
    size,
    enableUtcFallback = true,
    hideRelativeTerms = true,
    sectionType,
    sortingFn,
    shouldRender,
    getShimmerHeight,
    isLoading,
  } = options;

  return {
    id,
    enableColumnFilter: false,
    size,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={headerContentId} />
      </TableHeaderCellDiv>
    ),
    accessorFn: accessor,
    cell: function Cell({
      getValue,
      column: { id: columnId },
      row: { original },
      table,
    }) {
      const value = getValue<string | null>();

      const { userDefinedPrecision = defaultPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SectionType.Sales
      );

      const loading = isLoading ? isLoading(original) : !original;

      if (loading) {
        const shimmerHeight = getShimmerHeight
          ? getShimmerHeight(original)
          : SHIMMERING_DIV_HEIGHT_SINGLE_SALE;
        return <TableShimmeringDiv height={String(shimmerHeight)} />;
      }

      // Check if cell should be rendered
      if (shouldRender && !shouldRender(original)) {
        return null;
      }

      if (value == null) {
        return <BaseCell />;
      }

      return (
        <DateCell
          precision={userDefinedPrecision}
          date={value}
          enableUtcFallback={enableUtcFallback}
          hideRelativeTerms={hideRelativeTerms}
        />
      );
    },
    sortingFn: sortingFn ?? (safeSortDateTime as any),
  };
};
